import {debugWarn} from './debug';

/**
 * The in-progress signup, kept in `localStorage`.
 *
 * Signup spans a Paddle overlay, so it is the one flow in the app where a reload in the middle is
 * likely — and losing the token would strand a visitor who has just paid, with no way back to
 * "check your email" and no way to ask for the link again. The token grants nothing on its own
 * (provisioning re-checks Paddle for a real subscription), which is what makes parking it here safe.
 *
 * `paid` is the only thing the status endpoint cannot tell us: `pending` means both "never went
 * through checkout" and "checkout done, webhook not landed yet". The first still owes us a card;
 * the second owes us nothing, and is the difference between the checkout screen and the inbox one.
 */

const STORAGE_KEY = 'censorme.pending_signup';

export interface PendingSignup {
    token: string;
    email: string;
    name: string;
    /** ISO 8601. After this the token stops being redeemable and the signup has to be restarted. */
    expiresAt: string;
    /** Set once Paddle reported `checkout.completed`. */
    paid: boolean;
}

export function readPendingSignup(): PendingSignup | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as PendingSignup;
    if (!parsed?.token || !parsed?.email) {
      return null;
    }

    // An expired token cannot be redeemed or resumed, so it is worse than nothing: it would put the
    // visitor back on a dead "setting up your account" screen instead of the form.
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) {
      clearPendingSignup();
      return null;
    }

    return parsed;
  } catch (error) {
    debugWarn('[signupSession] Could not read the pending signup:', error);
    return null;
  }
}

export function writePendingSignup(signup: PendingSignup) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(signup));
  } catch (error) {
    debugWarn('[signupSession] Could not persist the pending signup:', error);
  }
}

export function markPendingSignupPaid() {
  const current = readPendingSignup();
  if (current) {
    writePendingSignup({...current, paid: true});
  }
}

export function clearPendingSignup() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    debugWarn('[signupSession] Could not clear the pending signup:', error);
  }
}
